import PropTypes from 'prop-types';
import React from 'react';
import Modal from 'components/Modal';
import ModalHeader from 'components/ModalHeader';
import ModalHeaderWithClose from 'components/ModalHeaderWithClose';
import InputWithIcon from 'components/InputWithIcon';
import {injectIntl, defineMessages, FormattedMessage} from 'react-intl';
import Icon from 'components/Icon';
import './StringValueInputModal.css';
import {fetchValuesForColumn} from 'state/dataSource/dataSourceService';
import debounce from 'lodash/debounce';
import {seconds} from 'utilities/date';
import {cancelRequest} from 'utilities/Request';
import {connect} from 'react-redux';
import {selectDateRangeFilterByCardId} from 'state/cardFilter/cardFilterSelectors';
import ButtonUnstyled from 'components/ButtonUnstyled';
import {selectIsDomoApp} from 'state/card/cardSelectors';

const EMPTY_LIMIT = 500;
const SEARCH_LIMIT = 500;

const messages = defineMessages({
  search: {
    defaultMessage: 'Search Values',
    id: 'StringValueInputModal__SearchLabel',
    description: 'Input placeholder to search column values',
  },
  remove: {
    defaultMessage: `Remove {column}`,
    id: 'StringValueInputModal__Remove',
    description: 'Label for remove icon',
  },
});

const GrossExtractedInputWithIconWrapperTillWeDropModalSelect = injectIntl(
  ({intl, ...rest}) => (
    <InputWithIcon
      placeholder={intl.formatMessage(messages.search)}
      {...rest}
    />
  )
);

class StringValueInputModal extends React.Component {
  constructor() {
    super(...arguments);

    this.state = {
      search: '',
      columnValues: [],
      selectedValues: new Set(this.props.values),
    };
  }

  componentDidMount() {
    this.searchValues();
  }

  componentDidUpdate({column, dataSourceId}) {
    if (
      column !== this.props.column ||
      dataSourceId !== this.props.dataSourceId
    ) {
      this.searchValues();
    }
  }

  componentWillUnmount() {
    this.cancelSearch();
  }

  handleSelect = value => {
    const selectedValues = new Set(this.state.selectedValues);
    if (this.state.selectedValues.has(value)) {
      selectedValues.delete(value);
    } else {
      selectedValues.add(value);
    }

    this.updateSelectedValues(selectedValues);
  };

  handleRemove = value => {
    const selectedValues = new Set(this.state.selectedValues);
    selectedValues.delete(value);
    this.updateSelectedValues(selectedValues);
  };

  updateSelectedValues(selectedValues) {
    this.setState({selectedValues}, () => {
      this.props.onSelect([...selectedValues]);
    });
  }

  cancelSearch() {
    cancelRequest(this.request);
  }

  searchValues = debounce(search => {
    this.cancelSearch();
    const {
      dataSourceId,
      column,
      pageId,
      cardId,
      dateRangeFilter,
      isDomoApp,
    } = this.props;

    // null dataSourceId means "all datasources"
    const dataSourceIds = dataSourceId ? [dataSourceId] : column.dataSourceIds;
    this.request = fetchValuesForColumn({
      column: column.id,
      dataSourceIds,
      cardId,
      limit: search ? SEARCH_LIMIT : EMPTY_LIMIT,
      pageId,
      search,
      dateRangeFilter,
      isDomoApp,
    });

    this.request.then(response => {
      if (!response.values) {
        return Promise.reject(new Error(response));
      }

      this.setState({columnValues: response.values});
    }, e => (e.statusText === 'abort' ? null : Promise.reject(e)));
  }, seconds(0.25));

  handleInputChange = search => {
    this.setState({search});
    this.searchValues(search);
  };

  render() {
    const {column, intl} = this.props;
    const {columnValues, selectedValues, search} = this.state;

    const selectedIcon = (
      <div>
        <Icon icon={Icon.OPTIONS.CHECK} size={Icon.SIZES.MICRO} />
      </div>
    );

    const pills = [...selectedValues].map(value => (
      <div className="StringValueInputModal__SelectedValue" key={value}>
        <div className="StringValueInputModal__SelectedValueText">{value}</div>
        <ButtonUnstyled
          onClick={this.handleRemove.bind(this, value)}
          title={intl.formatMessage(messages.remove, {column: value})}
        >
          <Icon
            className="StringValueInputModal__SelectedValueRemove"
            icon={Icon.OPTIONS.X}
            size={Icon.SIZES.NANO}
          />
        </ButtonUnstyled>
      </div>
    ));

    const options = columnValues.map(value => (
      <ButtonUnstyled
        key={value}
        onClick={this.handleSelect.bind(this, value)}
        className="StringValueInputModal__Option"
      >
        <div className="StringValueInputModal__OptionText">{value}</div>
        {selectedValues.has(value) ? selectedIcon : null}
      </ButtonUnstyled>
    ));

    const modalIcon = (
      <a className="StringValueInputModal__Close">
        <FormattedMessage
          id="StringValueInputModal__Close"
          description="Action to indicate a user is finished selecting filter options"
          defaultMessage="Done"
        />
      </a>
    );

    const moreValuesMessage = (
      <div className="StringValueInputModal__MoreValues">
        <FormattedMessage
          id="StringValueInputModal__MoreValues"
          description="Label to indicate more values are available through Search"
          defaultMessage="Search to see more values"
        />
      </div>
    );

    const isEmpty = selectedValues.size === 0;
    const hasMoreValues = this.state.columnValues.length >= EMPTY_LIMIT;

    return (
      <Modal isOpen={this.props.isOpen}>
        <ModalHeaderWithClose
          onClose={this.props.onRequestModalClose}
          icon={modalIcon}
        >
          <ModalHeader>{column.name}</ModalHeader>
        </ModalHeaderWithClose>
        {isEmpty ? null : (
          <div className="StringValueInputModal__SelectedValues">{pills}</div>
        )}
        <GrossExtractedInputWithIconWrapperTillWeDropModalSelect
          onInputChange={this.handleInputChange}
          value={search}
          icon={<Icon size={Icon.SIZES.MICRO} icon={Icon.OPTIONS.SEARCH} />}
        />
        {options}
        {hasMoreValues ? moreValuesMessage : null}
      </Modal>
    );
  }
}

StringValueInputModal.propTypes = {
  cardId: PropTypes.string,
  column: PropTypes.object.isRequired,
  dataSourceId: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  pageId: PropTypes.string,
  values: PropTypes.array.isRequired,
};

export default injectIntl(
  connect((state, {cardId}) => ({
    dateRangeFilter: selectDateRangeFilterByCardId(state, cardId),
    isDomoApp: selectIsDomoApp(state, cardId),
  }))(StringValueInputModal)
);
